export const EXAMPLES = [
  {
    equation: "x**2 - 4*x - 5 < 0",
    label: "x² − 4x − 5 < 0",
    accent: "purple",
    iconKey: "inequality",
  },
  {
    equation: "Area of a circle inscribed in square with a side of 8cm",
    label: "Area of a circle inscribed in square with a side of 8cm",
    accent: "blue",
    iconKey: "geometry",
  },
  {
    equation: "integrate(x*sin(x), x)",
    label: "∫ x · sin(x) dx",
    accent: "pink",
    iconKey: "calculus",
  },
]

export const LEVELS = {
  CIS: {
    "7class": { label: "7 класс" },
    "9class": { label: "8-9 класс" },
    student: { label: "Студент" },
  },
  USA: {
    algebra1: { label: "Grade 7" },
    algebra2: { label: "Grade 8-9" },
    precalculus: { label: "College" },
  },
}

export const DEFAULT_LEVEL = { CIS: "9class", USA: "algebra2" }
export const REGIONS = ["USA", "CIS"]

export const LEVEL_ITEMS = Object.fromEntries(
  REGIONS.map((region) => [region, Object.entries(LEVELS[region]).map(([value, { label }]) => ({ value, label }))])
)

export function answerText(answer) {
  if (!answer) return ""
  return answer.answer_latex || answer.answer?.symbolic || ""
}

export function regionForLevel(level) {
  return REGIONS.find((region) => LEVELS[region]?.[level]) || null
}

const BASE_URL =
  process.env.NODE_ENV === "production"
    ? "https://api.equationsolver.cc"
    : process.env.NODE_ENV === "staging"
      ? "https://homelab.equationsolver.cc"
      : "http://127.0.0.1:8000"

export const getServerUrl = (endpoint) => `${BASE_URL}/${endpoint}`
export const IS_WEB = typeof window !== "undefined" && window.location.protocol !== "chrome-extension:"

// Hard cap on user-attached images — big files also imply big base64 payloads.
export const MAX_ATTACH_BYTES = 10 * 1024 * 1024 // 10 MB
export const ALLOWED_IMAGE_TYPES = ["image/png", "image/jpeg", "image/webp", "image/gif", "image/bmp"]

// Decodes the file via <img> to reject corrupt bytes before they reach the cropper.
export async function validateImageFile(file) {
  if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
    throw new Error("Please pick an image (PNG, JPEG, WebP, GIF, or BMP).")
  }
  if (file.size > MAX_ATTACH_BYTES) {
    throw new Error(`That image is too big. Please pick one under ${MAX_ATTACH_BYTES / 1024 / 1024} MB.`)
  }
  const dataUrl = await new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result)
    reader.onerror = () => reject(new Error("Couldn't read that file. Please try another."))
    reader.readAsDataURL(file)
  })
  await new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve()
    img.onerror = () => reject(new Error("This image looks damaged. Please pick another."))
    img.src = dataUrl
  })
  return dataUrl
}

// Downscale large crops to cap OCR input tokens — vision models charge per tile.
const MAX_DIM = 1024

export async function getCroppedImg(imageSrc, pixelCrop) {
  const image = await new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve(img)
    img.onerror = reject
    img.src = imageSrc
  })

  const scale = Math.min(1, MAX_DIM / Math.max(pixelCrop.width, pixelCrop.height))
  const outW = Math.round(pixelCrop.width * scale)
  const outH = Math.round(pixelCrop.height * scale)

  const canvas = new OffscreenCanvas(outW, outH)
  const ctx = canvas.getContext("2d")
  ctx.drawImage(image, pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height, 0, 0, outW, outH)

  const blob = await canvas.convertToBlob({ type: "image/png" })
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result.split(",")[1])
    reader.onerror = reject
    reader.readAsDataURL(blob)
  })
}

export async function recognizeEquation(base64Image) {
  const response = await fetch(getServerUrl("api/recognize"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ image: base64Image }),
  })

  if (!response.ok) {
    const text = await response.text().catch(() => "")
    throw new Error(`OCR failed (${response.status}): ${text}`)
  }

  const data = await response.json()
  return data.equation
}
